import { Space, Tag } from 'antd';
import { useCallback, useEffect } from 'react';
import RevxTable, { TableColumnsType } from 'components/RevxTable';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useTranslation } from 'react-i18next';
import {
  Markup,
  MarkupTypeEnum,
  ShippingMarkupTableRecord,
} from 'domains/markupDomain';
import { RevxButton } from 'components/RevxButton';
import { isEmpty } from 'lodash-es';

const toShippingMarkupTableRecord = (m: Markup) => {
  return {
    ...m,
    orgCount: m.orgShippingFilters?.length || 0,
    carrierCount: isEmpty(m.orgShippingFilters)
      ? 0
      : m.orgShippingFilters.reduce((prev, curr) => ({
          orgId: 0,
          carrierTypes: [...prev.carrierTypes, ...curr.carrierTypes],
        })).carrierTypes.length,
  } as ShippingMarkupTableRecord;
};

const ShippingMarkupTable = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  // table data processing
  const data = useAppSelector((state) => {
    if (state.markup.revshopShippingMarkups?.content) {
      return state.markup.revshopShippingMarkups.content.map(
        toShippingMarkupTableRecord
      );
    } else {
      return [];
    }
  });
  const total = useAppSelector(
    (state) => state.markup.revshopShippingMarkups?.totalElements || 0
  );
  const currentPage = useAppSelector(
    (state) => state.markup.revshopShippingCurrentPage
  );
  const pageSize = useAppSelector(
    (state) => state.markup.revshopShippingPageSize
  );
  const loading = useAppSelector(
    (state) => state.loading.effects.markup.getRevshopShippingMarkups
  );

  useEffect(() => {
    dispatch.markup.getRevshopShippingMarkups({
      page: { currentPage, pageSize },
      query: { type: MarkupTypeEnum.REVSHOP_SHIPPING },
    });
  }, [currentPage, pageSize, dispatch.markup]);

  const onTableChange = useCallback(
    (pagination, filters, sorter, extra) => {
      switch (extra.action) {
        case 'paginate':
          const currentPage = pagination.current;
          const pageSize = pagination.pageSize;
          dispatch.markup.setState({
            revshopShippingCurrentPage: currentPage,
            revshopShippingPageSize: pageSize,
          });
          break;
      }
    },
    [dispatch.markup]
  );
  const columns: TableColumnsType<ShippingMarkupTableRecord> = [
    {
      title: t('common.id'),
      dataIndex: 'id',
      fixed: 'left',
    },
    {
      title: t('common.description'),
      dataIndex: 'description',
      width: 200,
    },
    {
      title: t('common.type'),
      dataIndex: 'isFree',
      render: (isFree) =>
        isFree
          ? t('platform.markup.labels.freeShipping')
          : t('platform.markup.labels.flatMarkup'),
    },
    {
      title: t('platform.markup.columns.orgCount'),
      dataIndex: 'orgCount',
    },
    {
      title: t('platform.markup.columns.carrierCount'),
      dataIndex: 'carrierCount',
    },

    {
      title: t('common.createdAt'),
      dataIndex: 'createAt',
      valueType: 'time',
    },
    {
      title: t('common.active'),
      fixed: 'right',
      dataIndex: 'active',
      render: (active) => (
        <Tag color={active ? 'green' : 'volcano'}>
          {active ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      fixed: 'right',
      render: (value, record) => (
        <Space size="small">
          <RevxButton
            type="link"
            onClick={() =>
              dispatch.markup.setState({
                curShippingMarkup: {
                  ...record,
                },
              })
            }
          >
            {t('common.view')}
          </RevxButton>
        </Space>
      ),
    },
  ];

  return (
    <RevxTable<ShippingMarkupTableRecord>
      loading={loading}
      columns={columns}
      dataSource={data}
      pagination={{
        pageSize,
        total,
      }}
      onChange={onTableChange}
    />
  );
};

export default ShippingMarkupTable;
